import ConfigProvider from '@core/services/ConfigProvider';

const client = ConfigProvider.value('client');

// Get module enablement from build-time environment variables
// These are replaced by Vite at build time
const portfolioModuleEnabled =
  import.meta.env.VITE_APP_PORTFOLIO_MODULE_ENABLED !== 'false';
const dataDictionaryModuleEnabled =
  import.meta.env.VITE_APP_DATADICTIONARY_MODULE_ENABLED !== 'false';
const subscriptionManagerModuleEnabled =
  import.meta.env.VITE_APP_SUBSCRIPTION_MANAGER_MODULE_ENABLED !== 'false';
const azureAdModuleEnabled =
  import.meta.env.VITE_APP_AZUREAD_MODULE_ENABLED !== 'false';

// Build modules array based on enabled modules
const modules = [
  'core',
  'domain',
  'internalDevportal',
  ...(portfolioModuleEnabled ? ['portfolio'] : []),
  ...(dataDictionaryModuleEnabled ? ['dataDictionary'] : []),
  ...(subscriptionManagerModuleEnabled ? ['subscriptionManager'] : []),
  ...(azureAdModuleEnabled ? ['aad'] : []),
  'externalDevportal',
];

// Get the client from build-time environment variable
// This is replaced by Vite at build time
const buildTimeClient = import.meta.env.VITE_APP_CLIENT;

// Pre-load client language files using import.meta.glob for static analysis
// Only define the glob for the specific client being built to prevent
// Vite from including dynamic imports for other clients' files
// Using eager: true to ensure files are bundled and not lazy-loaded
const gastatLanguageFiles =
  buildTimeClient === 'gastat'
    ? import.meta.glob(
        '../../externalDevportal/clients/gastat/languages/*.json',
        {eager: true, import: 'default'},
      )
    : {};

const apidexLanguageFiles =
  buildTimeClient === 'apidex'
    ? import.meta.glob(
        '../../externalDevportal/clients/apidex/languages/*.json',
        {eager: true, import: 'default'},
      )
    : {};

const belastingdienstLanguageFiles =
  buildTimeClient === 'belastingdienst'
    ? import.meta.glob(
        '../../externalDevportal/clients/belastingdienst/languages/*.json',
        {eager: true, import: 'default'},
      )
    : {};

const dgaLanguageFiles =
  buildTimeClient === 'dga'
    ? import.meta.glob('../../externalDevportal/clients/dga/languages/*.json', {
        eager: true,
        import: 'default',
      })
    : {};

// Select the appropriate language files based on build-time client
const getClientLanguageFiles = () => {
  if (buildTimeClient === 'gastat') {
    return gastatLanguageFiles;
  }
  if (buildTimeClient === 'apidex') {
    return apidexLanguageFiles;
  }
  if (buildTimeClient === 'belastingdienst') {
    return belastingdienstLanguageFiles;
  }
  if (buildTimeClient === 'dga') {
    return dgaLanguageFiles;
  }
  return {};
};
const clientLanguageFiles = getClientLanguageFiles();

// Create separate globs for each module to allow conditional loading
// Vite will tree-shake unused globs, so only enabled modules' files will be included
const coreLanguageFiles = import.meta.glob('../../core/languages/*.json', {
  eager: true,
  import: 'default',
});

const domainLanguageFiles = import.meta.glob('../../domain/languages/*.json', {
  eager: true,
  import: 'default',
});

const internalDevportalLanguageFiles = import.meta.glob(
  '../../internalDevportal/languages/*.json',
  {
    eager: true,
    import: 'default',
  },
);

const portfolioLanguageFiles = portfolioModuleEnabled
  ? import.meta.glob('../../portfolio/languages/*.json', {
      eager: true,
      import: 'default',
    })
  : {};

const dataDictionaryLanguageFiles = dataDictionaryModuleEnabled
  ? import.meta.glob('../../dataDictionary/languages/*.json', {
      eager: true,
      import: 'default',
    })
  : {};

const subscriptionManagerLanguageFiles = subscriptionManagerModuleEnabled
  ? import.meta.glob('../../subscriptionManager/languages/*.json', {
      eager: true,
      import: 'default',
    })
  : {};

const aadLanguageFiles = azureAdModuleEnabled
  ? import.meta.glob('../../aad/languages/*.json', {
      eager: true,
      import: 'default',
    })
  : {};

const externalDevportalLanguageFiles = import.meta.glob(
  '../../externalDevportal/languages/*.json',
  {
    eager: true,
    import: 'default',
  },
);

// Combine all enabled module language files into one object
// This ensures all language files are in one chunk when eager: true
const moduleLanguageFiles = {
  ...coreLanguageFiles,
  ...domainLanguageFiles,
  ...internalDevportalLanguageFiles,
  ...(portfolioModuleEnabled ? portfolioLanguageFiles : {}),
  ...(dataDictionaryModuleEnabled ? dataDictionaryLanguageFiles : {}),
  ...(subscriptionManagerModuleEnabled ? subscriptionManagerLanguageFiles : {}),
  ...(azureAdModuleEnabled ? aadLanguageFiles : {}),
  ...externalDevportalLanguageFiles,
};

const loadModuleMessages = (language: string, module: string) => {
  try {
    // Skip loading messages for disabled modules (double-check for safety)
    if (module === 'portfolio' && !portfolioModuleEnabled) {
      return {};
    }
    if (module === 'dataDictionary' && !dataDictionaryModuleEnabled) {
      return {};
    }
    if (module === 'subscriptionManager' && !subscriptionManagerModuleEnabled) {
      return {};
    }
    if (module === 'aad' && !azureAdModuleEnabled) {
      return {};
    }

    // Find the language file by checking if the key contains the module path and language
    // Glob keys can be in different formats:
    // - For core: ./en-GB.json or ../../core/languages/en-GB.json
    // - For other modules: ../../portfolio/languages/en-GB.json
    const fileName = `${language}.json`;
    const key = Object.keys(moduleLanguageFiles).find(k => {
      // For core module, keys are like ./en-GB.json (relative from core/languages/)
      if (module === 'core') {
        return (
          k.endsWith(fileName) &&
          (k.includes('core/languages/') || k.startsWith('./'))
        );
      }
      // For other modules, check if key contains the module path
      const modulePath = `${module}/languages/${fileName}`;
      return (
        k.includes(modulePath) ||
        (k.endsWith(fileName) && k.includes(`/${module}/`))
      );
    });

    if (key) {
      const moduleMessages: any = moduleLanguageFiles[key];
      return moduleMessages ?? {};
    }
    return {};
  } catch {
    console.warn(`Error: missing language file for ${language} in ${module}`);
    return {};
  }
};

const loadClientMessages = (language: string) => {
  if (
    !client ||
    !clientLanguageFiles ||
    Object.keys(clientLanguageFiles).length === 0
  ) {
    return {};
  }

  try {
    // Find the language file for the current client
    const expectedSuffix = `clients/${client}/languages/${language}.json`;
    const key = Object.keys(clientLanguageFiles).find(k =>
      k.endsWith(expectedSuffix),
    );

    // Check if the file exists in the pre-loaded glob (eager loaded)
    if (key) {
      const clientMessages = clientLanguageFiles[key];
      return clientMessages ?? {};
    }

    return {};
  } catch {
    return {};
  }
};

const getMessages = async () => {
  const messages: Record<string, Record<string, unknown>> = {
    'en-GB': {},
    'nl-NL': {},
    'es-ES': {},
    'se-SE': {},
    'de-DE': {},
    'fr-FR': {},
    'ar-SA': {},
  };

  for (const language of Object.keys(messages)) {
    for (const module of modules) {
      const moduleMessages = loadModuleMessages(language, module);
      if (Object.keys(moduleMessages).length > 0) {
        messages[language] = {
          ...messages[language],
          ...moduleMessages,
        };
      }
    }

    const clientMessages = loadClientMessages(language);
    if (Object.keys(clientMessages).length > 0) {
      messages[language]['externalDevPortal'] = {
        ...messages[language]['externalDevPortal'],
        ...clientMessages,
      };
    }
  }

  // Add 'en' locale as an alias for 'en-GB' to handle vue-i18n fallback
  if (messages['en-GB'] && Object.keys(messages['en-GB']).length > 0) {
    messages['en'] = messages['en-GB'];
  }

  return messages;
};

export default getMessages;
